/**
 * scanPhoto.js — the React side of the one shared document scanner.
 *
 * The scanner itself is a plain-script global (no bundler dependency):
 * DocumentScanner (capture/review UI) + ScanAdapters (per-destination last
 * mile). Host pages that already include those scripts need nothing more;
 * every other mount loads them on first use from the same origin and falls
 * back to the native capture input when that fails, so the camera never
 * goes dead because a script did.
 *
 * Each caller keeps its own sink: confirmed documents arrive as Files into
 * the SAME handler the file input used. A multi-page scanner document arrives
 * as one PDF; a one-page document remains a JPEG.
 */

const SCANNER_SCRIPTS = [
  '/js/document-scanner/document-scanner.js',
  '/js/document-scanner/scan-adapters.js',
];

export function scannerAvailable(scope) {
  const w = scope || (typeof window !== 'undefined' ? window : null);
  return !!w && !!w.ScanAdapters && !!w.DocumentScanner;
}

function loadScriptOnce(doc, src) {
  return new Promise((resolve, reject) => {
    try {
      const existing = doc.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset && existing.dataset.scanLoaded === '1') {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener('error', () => reject(new Error(`scanner script failed: ${src}`)), { once: true });
        return;
      }
      const el = doc.createElement('script');
      el.src = src;
      el.async = false;
      el.dataset.scanLoading = '1';
      el.addEventListener('load', () => {
        el.dataset.scanLoaded = '1';
        resolve();
      }, { once: true });
      el.addEventListener('error', () => reject(new Error(`scanner script failed: ${src}`)), { once: true });
      doc.head.appendChild(el);
    } catch (err) {
      reject(err);
    }
  });
}

export async function ensureScanner(scope, doc) {
  const w = scope || (typeof window !== 'undefined' ? window : null);
  if (!w) return false;
  if (scannerAvailable(w)) return true;
  // An explicit null doc means "no DOM to load into" (tests); only fall
  // back to ambient documents when the caller passed nothing at all.
  const d = doc !== undefined
    ? doc
    : (w.document || (typeof document !== 'undefined' ? document : null));
  if (!d || !d.createElement || !d.head) return false;
  try {
    for (const src of SCANNER_SCRIPTS) {
      // Sequential: adapters reference DocumentScanner only at open() time,
      // but ordered loading keeps failure attribution exact.
      // eslint-disable-next-line no-await-in-loop
      await loadScriptOnce(d, src);
    }
  } catch (_) {
    return scannerAvailable(w);
  }
  return scannerAvailable(w);
}

/**
 * Open the shared scanner for a document-import camera entry.
 *
 * {
 *   destinationKind, stem, capabilities, confirmLabel,
 *   onFiles(files, session), onCancel,
 *   onError(error),
 *   fallback(),            // native capture input when the scanner is unusable
 *   scope, doc,             // injectable for tests
 * }
 * Resolves true when the scanner opened, false when the fallback ran.
 */
export async function takeScanPhoto(options) {
  const opts = options || {};
  const w = opts.scope || (typeof window !== 'undefined' ? window : null);
  const fallback = typeof opts.fallback === 'function' ? opts.fallback : () => {};
  let ready = false;
  try {
    ready = await ensureScanner(w, opts.doc);
  } catch (_) {
    ready = false;
  }
  if (!ready || !w || !w.ScanAdapters || typeof w.ScanAdapters.openImportCamera !== 'function') {
    fallback();
    return false;
  }
  try {
    w.ScanAdapters.openImportCamera({
      destinationKind: opts.destinationKind || 'item_import',
      stem: opts.stem || 'scan',
      capabilities: opts.capabilities || w.ScanAdapters.CAPABILITIES.importCamera,
      confirmLabel: opts.confirmLabel || 'Use these scans',
      onFiles: opts.onFiles,
      onCancel: opts.onCancel,
      onError: opts.onError,
    });
    return true;
  } catch (_) {
    fallback();
    return false;
  }
}
